import { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import { handleError } from "../../utils/helper";
import { useAppKitAccount } from "@reown/appkit/react";
import { usePublicClient, useWriteContract } from "wagmi";
import { DAO_CONTRACT_ADDRESS } from "../../utils/constant";
import { config } from "../../config";
import { readContract } from "@wagmi/core";
import { DAO_ABI } from "../../Contract/DAO_ABI";

const SetQuorumModal = ({ show, handleClose, initialQuorum }) => {
  const [quorumPercent, setQuorumPercent] = useState(initialQuorum);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const { address } = useAppKitAccount();
  const publicClient = usePublicClient();
  const { writeContractAsync } = useWriteContract();

  const validate = () => {
    const newErrors = {};

    if (!quorumPercent) {
      newErrors.quorumPercent = "Quorum is required";
    } else if (isNaN(Number(quorumPercent))) {
      newErrors.quorumPercent = "Must be a number";
    } else if (Number(quorumPercent) < 1 || Number(quorumPercent) > 100) {
      newErrors.quorumPercent = "Must be between 1 and 100";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSetQuorum = async () => {
    if (!validate()) return;

    try {
      setLoading(true);

      const contractData = {
        abi: DAO_ABI,
        address: DAO_CONTRACT_ADDRESS,
        functionName: "setQuorumPercentage", // <-- ensure matches your contract
        args: [Number(quorumPercent)],
      };

      await readContract(config, { ...contractData, account: address });

      const tx = await writeContractAsync(contractData);
      const receipt = await publicClient.waitForTransactionReceipt({
        hash: tx,
      });

      if (receipt?.status === "success") {
        toast.success("Quorum updated successfully");

        handleClose();
      }
    } catch (error) {
      const err = handleError(error?.cause?.data);
      console.log("Quorum update error:", err);
      toast.error(err || "Transaction failed");
    } finally {
      setLoading(false);
      setErrors({});
    }
  };

  const handleQuorum = (e) => {
    const rawValue = e.target.value;
    // Allow empty (for clearing) or check if it's a valid integer
    const numValue = parseInt(rawValue, 10);

    // Block non-integer input (e.g., decimals, letters) – only allow digits or empty
    if (rawValue !== '' && (isNaN(numValue) || rawValue.includes('.'))) {
      // Revert to previous valid state (or do nothing – input won't update)
      e.target.value = quorumPercent;  // Fallback to current state
      return;
    }

    const newErrors = {};
    if (rawValue === '') {
      newErrors.quorumPercent = "Quorum is required";
    } else if (numValue < 1 || numValue > 100) {
      newErrors.quorumPercent = "Quorum must be between 1 and 100";
    }

    setErrors(newErrors);
    setQuorumPercent(rawValue);  // Set only if valid (empty or 1-100 integer)
  };

  return (
    <>
      {loading && <Loader />}
      <Modal
        show={show}
        onHide={() => {
          handleClose();
          setErrors({});
        }}>
        <Modal.Header closeButton>
          <Modal.Title>Set Quorum Percentage</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Quorum (%)</Form.Label>
              <Form.Control
                type="number"
                value={quorumPercent}
                placeholder="Enter quorum %"
                min={1}
                max={100}
                step="1"  // Enforces whole numbers (no decimals)
                onChange={(e) => handleQuorum(e)}
                className={`${errors.quorumPercent ? "is-invalid" : ""}`}
              />
              {errors.quorumPercent && (
                <div className="invalid-feedback">{errors.quorumPercent}</div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => {
              handleClose();
              setErrors({});
            }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSetQuorum}>
            Set Quorum
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SetQuorumModal;
